import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from '../api/axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ChevronLeft, ChevronRight, FlaskConical, FileText, CalendarDays } from 'lucide-react';
import {
  startOfWeek, endOfWeek, addWeeks, subWeeks, format, isSameDay,
  eachDayOfInterval, isWeekend
} from 'date-fns';
import { es } from 'date-fns/locale';

function parseDate(str) {
  if (!str) return null;
  try {
    if (str.includes('/')) {
      const [d, m, y] = str.split('/');
      return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    }
    return new Date(str);
  } catch {
    return null;
  }
}

export default function Estabilidades() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeekStart, setCurrentWeekStart] = useState(
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );

  useEffect(() => {
    const fetchSamples = async () => {
      try {
        const res = await axios.get('/work-samples');
        const estab = res.data.filter(s => s.destination === 'ESTABILIDADES');
        setSamples(estab);
      } catch (err) {
        console.error('Error fetching samples:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSamples();
  }, []);

  const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start: currentWeekStart, end: weekEnd })
    .filter(d => !isWeekend(d));

  const events = useMemo(() => {
    const result = [];
    for (const sample of samples) {
      // Salidas de cámara
      if (Array.isArray(sample.fechas_salida_camara)) {
        for (const sc of sample.fechas_salida_camara) {
          const date = parseDate(sc.fecha);
          if (date) {
            result.push({
              type: 'salida',
              label: `Salida Camara ${sc.numero}`,
              date,
              metodo: sc.metodo,
              sample,
            });
          }
        }
      }
      // F. Informe
      const informeDate = parseDate(sample.fecha_informe_estabilidades);
      if (informeDate) {
        result.push({
          type: 'informe',
          label: 'Informe',
          date: informeDate,
          sample,
        });
      }
    }
    return result;
  }, [samples]);

  const weekEvents = useMemo(() => {
    return events.filter(e =>
      weekDays.some(day => isSameDay(day, e.date))
    );
  }, [events, weekDays]);

  const salidaCount = weekEvents.filter(e => e.type === 'salida').length;
  const informeCount = weekEvents.filter(e => e.type === 'informe').length;

  const goToEdit = (sample) => {
    navigate('/entrada-datos', {
      state: { editSample: sample, returnTo: '/estabilidades' }
    });
  };

  const weekLabel = `${format(currentWeekStart, "d MMM", { locale: es })} - ${format(weekDays[weekDays.length - 1], "d MMM yyyy", { locale: es })}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="estabilidades-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FlaskConical className="h-6 w-6 text-indigo-600" />
            Estabilidades
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Planificador semanal de salidas de camara e informes
          </p>
        </div>
      </div>

      {/* Week Navigator */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentWeekStart(subWeeks(currentWeekStart, 1))}
              data-testid="prev-week-btn"
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Semana anterior
            </Button>

            <div className="text-center">
              <div className="flex items-center gap-2 justify-center">
                <CalendarDays className="h-5 w-5 text-indigo-600" />
                <span className="font-semibold text-lg capitalize">{weekLabel}</span>
              </div>
              <div className="flex gap-3 mt-1 justify-center">
                <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                  {salidaCount} {salidaCount === 1 ? 'salida' : 'salidas'}
                </Badge>
                <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
                  {informeCount} {informeCount === 1 ? 'informe' : 'informes'}
                </Badge>
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentWeekStart(addWeeks(currentWeekStart, 1))}
              data-testid="next-week-btn"
            >
              Semana siguiente <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>

          {/* Today button */}
          <div className="flex justify-center mt-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-indigo-600"
              onClick={() => setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}
              data-testid="today-btn"
            >
              Ir a esta semana
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Week Grid */}
      <div className="grid grid-cols-5 gap-3" data-testid="week-grid">
        {weekDays.map((day) => {
          const dayEvents = weekEvents
            .filter(e => isSameDay(e.date, day))
            .sort((a, b) => a.type === 'salida' ? -1 : 1);
          const isToday = isSameDay(day, new Date());

          return (
            <div
              key={day.toISOString()}
              className={`rounded-lg border min-h-[200px] ${
                isToday ? 'border-indigo-400 bg-indigo-50/30 ring-1 ring-indigo-200' : 'border-gray-200 bg-white'
              }`}
              data-testid={`day-col-${format(day, 'yyyy-MM-dd')}`}
            >
              {/* Day Header */}
              <div className={`px-3 py-2 border-b text-center rounded-t-lg ${
                isToday ? 'bg-indigo-100' : 'bg-gray-50'
              }`}>
                <p className="text-xs text-gray-500 uppercase">
                  {format(day, 'EEEE', { locale: es })}
                </p>
                <p className={`text-lg font-bold ${isToday ? 'text-indigo-700' : 'text-gray-800'}`}>
                  {format(day, 'd')}
                </p>
              </div>

              {/* Events */}
              <div className="p-2 space-y-2">
                {dayEvents.length === 0 && (
                  <p className="text-xs text-gray-300 text-center py-4 italic">
                    Sin eventos
                  </p>
                )}
                {dayEvents.map((event, idx) => (
                  <button
                    key={`${event.sample.id}-${event.type}-${idx}`}
                    onClick={() => goToEdit(event.sample)}
                    className={`w-full text-left rounded-md p-2.5 transition-all hover:shadow-md hover:scale-[1.02] cursor-pointer border ${
                      event.type === 'salida'
                        ? 'bg-blue-50 border-blue-200 hover:bg-blue-100'
                        : 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100'
                    }`}
                    data-testid={`event-card-${event.sample.id}-${event.type}-${idx}`}
                  >
                    <div className="flex items-start gap-1.5">
                      {event.type === 'salida' ? (
                        <FlaskConical className="h-3.5 w-3.5 text-blue-600 mt-0.5 flex-shrink-0" />
                      ) : (
                        <FileText className="h-3.5 w-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className={`text-xs font-semibold leading-tight ${
                          event.type === 'salida' ? 'text-blue-700' : 'text-emerald-700'
                        }`}>
                          {event.label}
                        </p>
                        <p className="text-xs text-gray-700 truncate mt-0.5" title={event.sample.client}>
                          {event.sample.client}
                        </p>
                        <p className="text-xs text-gray-500 truncate" title={event.sample.product}>
                          {event.sample.product}
                        </p>
                        {event.sample.num_ensayo && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            N GOYA: {event.sample.num_ensayo}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {samples.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <FlaskConical className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">No hay muestras de Estabilidades registradas</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate('/entrada-datos')}
            >
              Crear muestra
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
